import { useLocation, NavLink, useNavigate } from 'react-router-dom'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useBriefingStats } from '../../hooks/useBriefingStats'
import { useAuth, type Permissoes } from '../../hooks/useAuth'

// ─── Tipos ────────────────────────────────────────────────
export interface AppUser {
  id: string
  email: string
  nome: string
  cargo: string
}

export interface NavItem {
  to: string
  icon: string
  label: string
}

interface TopNavItemProps extends NavItem {}

interface StatChipProps {
  value: number
  label: string
  urgent?: boolean
}

interface UserAvatarProps {
  user: AppUser | null
}

export interface UserSignOutProps {
  user: AppUser | null
  onSignOut: () => void
}

const LBL_CLS = 'text-[9px] font-bold uppercase tracking-[0.12em] whitespace-nowrap'

export const NAV_MAIN: NavItem[] = [
  { to: '/hoje',      icon: 'dashboard',      label: 'Dashboard' },
  { to: '/projetos',  icon: 'folder',         label: 'Projetos'  },
  { to: '/clientes',  icon: 'group',          label: 'Clientes'  },
  { to: '/tarefas',   icon: 'check_circle',   label: 'Tarefas'   },
  { to: '/agenda',    icon: 'calendar_month', label: 'Agenda'    },
  { to: '/entregas',  icon: 'send',           label: 'Entregas'  },
  { to: '/notas',     icon: 'edit_note',      label: 'Notas'     },
]

export const NAV_SECONDARY: NavItem[] = [
  { to: '/apps',          icon: 'apps',     label: 'Apps Internos' },
  { to: '/financeiro',    icon: 'payments', label: 'Financeiro'    },
  { to: '/admin',         icon: 'settings', label: 'Admin'         },
  { to: '/configuracoes', icon: 'tune',     label: 'Configurações' },
]

// ─── Filtro de permissão por rota ─────────────────────────
const ROUTE_PERMISSION: Record<string, keyof Omit<Permissoes, 'projetos'> | 'projetos'> = {
  '/projetos':      'projetos',
  '/clientes':      'clientes',
  '/financeiro':    'financeiro',
  '/configuracoes': 'configuracoes',
  '/apps':          'propostas',
}

export function filterNavByPermission(items: NavItem[], can: (m: keyof Omit<Permissoes, 'projetos'>) => boolean, projetoScope: string): NavItem[] {
  return items.filter(item => {
    const perm = ROUTE_PERMISSION[item.to]
    if (!perm) return true
    if (perm === 'projetos') return projetoScope !== 'nenhum'
    return can(perm as keyof Omit<Permissoes, 'projetos'>)
  })
}

// ─── Componentes auxiliares ───────────────────────────────
function TopNavItem({ to, icon, label }: TopNavItemProps) {
  const { pathname } = useLocation()
  const isActive = pathname === to || pathname.startsWith(to + '/')

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={to}
          className={[
            'flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150',
            isActive
              ? 'text-[var(--color-hero)] bg-[var(--color-surface)]'
              : 'text-[var(--color-muted)] hover:text-white hover:bg-[var(--color-surface)]',
          ].join(' ')}
        >
          <span className="material-symbols-outlined text-xl">{icon}</span>
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  )
}

function StatChip({ value, label, urgent }: StatChipProps) {
  return (
    <div className="flex flex-col items-center min-w-7">
      <span className={`text-[13px] font-bold leading-none ${urgent ? 'text-[var(--color-vermelho)]' : 'text-[#f0f0f0]'}`}>{value}</span>
      <span className="text-[8px] font-bold uppercase tracking-[0.09em] text-[#555] leading-tight">{label}</span>
    </div>
  )
}

export function UserAvatar({ user }: UserAvatarProps) {
  return (
    <div className="w-7 h-7 rounded-full shrink-0 bg-[var(--color-element)] border border-[var(--color-line)] flex items-center justify-center">
      <span className="text-[11px] font-bold text-white/55">
        {user?.nome?.charAt(0).toUpperCase() ?? '?'}
      </span>
    </div>
  )
}

// ─── TopbarMode ───────────────────────────────────────────
export function TopbarMode({ user, onSignOut }: UserSignOutProps) {
  const navigate = useNavigate()
  const stats = useBriefingStats()
  const { canShowroom, can, projetoScope } = useAuth()
  const mainFiltered = filterNavByPermission(NAV_MAIN, can, projetoScope)
  const secFiltered  = filterNavByPermission(NAV_SECONDARY, can, projetoScope)

  return (
    <header className="h-14 shrink-0 flex items-center gap-2 px-4 border-b border-[var(--color-line)] bg-[var(--color-bg)] z-50">
      <img src="/assets/images/logo-ifp.png" alt="IFP" onClick={() => navigate('/hoje')} className="w-9 h-auto object-contain shrink-0 cursor-pointer" />
      <div className="w-px h-6 bg-[var(--color-line)] mx-1" />

      <nav className="flex items-center gap-1">
        {mainFiltered.map(item => <TopNavItem key={item.to} {...item} />)}
      </nav>

      {stats && (
        <div className="flex items-center gap-4 px-4 ml-2 border-l border-[var(--color-line)]">
          <StatChip value={stats.projetos} label="projetos" />
          <StatChip value={stats.atrasadas} label="atrasadas" urgent={stats.atrasadas > 0} />
          <StatChip value={stats.hoje} label="hoje" />
        </div>
      )}

      <div className="flex items-center gap-1 ml-auto shrink-0">
        {canShowroom && <TopNavItem to="/showroom" icon="slideshow" label="Showroom" />}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label="Mais opções" className="flex items-center justify-center w-9 h-9 rounded-lg text-[var(--color-muted)] hover:text-white hover:bg-[var(--color-surface)] transition-colors bg-transparent border-none cursor-pointer">
              <span className="material-symbols-outlined text-xl">more_horiz</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {secFiltered.map(item => (
              <DropdownMenuItem key={item.to} onClick={() => navigate(item.to)} className="gap-2 cursor-pointer">
                <span className="material-symbols-outlined text-lg">{item.icon}</span>
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label="Menu do usuário" className="p-1 rounded-full hover:ring-2 hover:ring-[var(--color-line)] transition-all bg-transparent border-none cursor-pointer">
              <UserAvatar user={user} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {user && (
              <>
                <div className="px-3 py-2">
                  <p className="text-[11px] font-bold text-[#f0f0f0]">{user.nome}</p>
                  <p className="text-[9px] text-[#555]">{user.cargo}</p>
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={onSignOut} className="gap-2 cursor-pointer text-[var(--color-muted)] hover:text-white">
              <span className="material-symbols-outlined text-lg">logout</span>
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
